// SetupProfilePage.js - Account exists but has no user_profiles row
// SECURITY (AUDIT F12): this page used to print copy-paste SQL that created a
// 'super_admin' profile for the logged-in user. Profile provisioning must only
// happen through admin flows (User Management / admin-create-user edge
// function) with the role chosen by an existing admin.
import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import AppButton from '../shared/AppButton';

const SetupProfilePage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkProfile = async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (!session?.user) {
                    setLoading(false);
                    return;
                }
                setUser(session.user);
                const { data: profileData, error } = await supabase
                    .from('user_profiles')
                    .select('*')
                    .eq('id', session.user.id)
                    .single();
                if (!error && profileData) {
                    setProfile(profileData);
                }
            } catch (err) {
                console.error('Error:', err);
            } finally {
                setLoading(false);
            }
        };
        checkProfile();
    }, []);

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ minHeight: '100vh', p: 4 }}>
                <Paper sx={{ maxWidth: 600, mx: 'auto', p: 4 }}>
                    <Alert severity="warning">
                        You are not logged in. Please <a href="/login">login</a> first.
                    </Alert>
                </Paper>
            </Box>
        );
    }

    if (profile) {
        return (
            <Box sx={{ minHeight: '100vh', p: 4 }}>
                <Paper sx={{ maxWidth: 600, mx: 'auto', p: 4 }}>
                    <Alert severity="success" sx={{ mb: 3 }}>
                        Your user profile is already set up!
                    </Alert>
                    <Typography variant="h6" gutterBottom>
                        Profile Details:
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2"><strong>Email:</strong> {user.email}</Typography>
                        <Typography variant="body2"><strong>Role:</strong> {profile.role}</Typography>
                        <Typography variant="body2"><strong>Display Name:</strong> {profile.display_name || 'Not set'}</Typography>
                        <Typography variant="body2"><strong>Status:</strong> {profile.is_active ? 'Active' : 'Inactive'}</Typography>
                    </Box>
                    <AppButton variant="primary" onClick={() => navigate('/')}>
                        Go to Dashboard
                    </AppButton>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', p: 4, bgcolor: 'background.default' }}>
            <Paper sx={{ maxWidth: 600, mx: 'auto', p: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Account Not Provisioned
                </Typography>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Your account (<strong>{user.email}</strong>) exists but has not been
                    linked to an employee profile yet.
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Ask your company administrator or HR team to:
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 3, color: 'text.secondary' }}>
                    <Typography component="li" variant="body2">
                        Open <strong>Settings → User Management</strong> and edit or create your profile, or
                    </Typography>
                    <Typography component="li" variant="body2">
                        Use the <strong>Add User</strong> flow (admin-create-user), which links your login to an
                        employee record and assigns your role.
                    </Typography>
                </Box>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Roles are assigned by administrators only. If you believe your role is wrong,
                    contact HR — do not attempt to modify database records directly.
                </Alert>
                <AppButton variant="secondary" onClick={() => window.location.reload()}>
                    Refresh
                </AppButton>
            </Paper>
        </Box>
    );
};

export default SetupProfilePage;
